// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import RedirectToSignIn from "@bg/App/Components/RedirectToSignIn/RedirectToSignIn";
import ProductAvailability, {
    ProductAvailabilityStyles,
    productAvailabilityStyles,
} from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import xScreen from "@bg/XDL/Utilities/xScreen";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Clickable from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // Xng-Change
    editable?: boolean;
    pickupOnlyLabel?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // Xng-Change
    isAuth: getSession(state).isAuthenticated,
    customSettings: getSettingsCollection(state).customSettings as any,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface PickUpOnlyFlag {
    wrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    icon?: IconProps;
}

export interface CartLineCardExpandedStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productBrandAndDescriptionGridItem?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    productPartNumbersWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    pricing?: InjectableCss;
    priceQtywrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    quantityGridItem?: InjectableCss;
    extendedUnitNetPriceGridItem?: InjectableCss;
    listAndRemoveWrapper?: InjectableCss;
    descAndNumbersWrapper?: InjectableCss;
    cartLineInfoGridItem?: InjectableCss;
    cartLineInfoContainer?: InjectableCss;
    productInfoGridItem?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productBrand?: ProductBrandStyles;
    configurationGridItem?: InjectableCss;
    configurationOptionText?: TypographyProps;
    quantityAndExtendedUnitNetPriceGridItem?: InjectableCss;
    quantityAndExtendedUnitNetPriceContainer?: InjectableCss;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: InjectableCss;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: InjectableCss;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    costCodeGridItem?: InjectableCss;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    pickUpOnlyFlag?: PickUpOnlyFlag;
    availabilityContainer?: InjectableCss;
    availabilityWrapper?: InjectableCss;
    pickUpOnlyFlagWrapper?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 11px 10px 20px 0;
            border-bottom: 1px solid ${getColor("common.ui2")};
            position: relative;
            @media print {
                width: 100%;
                display: block !important;
                border-bottom: 1px solid #b0b0b7;
                page-break-inside: avoid;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            width: calc(100% - 405px);
            padding-right: 10px;
            ${xScreen("lte", 900)} {
                width: 100%;
                padding-right: 20px;
            }

            ${xScreen("lte", 900)} {
                padding: 0;
            }

            @media print {
                padding: 0;
                width: 100%;
            }
        `,
    },
    leftSection: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding-left: 10px;
            ${xScreen("lte", 900)} {
                width: 100%;
                padding-left: 0;
                margin-top: 15px;
            }

            @media print {
                display: block;
                padding: 0;
                width: 100%;
            }
        `,
    },
    imageWrapper: {
        css: css`
            min-width: 114px;
            height: 114px;
            display: flex;
            align-items: center;
            justify-content: center;
            ${xScreen("lte", 900)} {
                height: 76px;
                min-width: 76px;
                margin-right: 12px;
            }

            @media print {
                display: inline-block;
                max-width: 114px;
                min-width: 114px !important;
                margin-right: 30px;
                float: left;
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
                min-width: 114px;
                max-width: 114px;
                height: 114px;
                ${xScreen("lte", 900)} {
                    min-width: 76px;
                    max-width: 76px;
                    height: 76px;
                }
            `,
        },
        image: {
            width: "100%",
            cloudinary: true,
            // to avoid any printing issues
            disableLazyLoad: true,
            cloudinaryWidth: "114",
            css: css`
                img {
                    max-width: 114px;
                    max-height: 114px;
                    object-fit: contain;
                }
                ${xScreen("lte", 900)} {
                    img {
                        max-width: 76px;
                        max-height: 76px;
                    }
                }
            `,
        },
    },
    descAndNumbersWrapper: {
        css: css`
            flex-grow: 1;
            margin-left: 30px;
            ${xScreen("lte", 900)} {
                margin-left: 0;
            }
            @media print {
                width: calc(100% - 150px);
                clear: right;
            }
        `,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            margin-bottom: 6px;
        `,
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: fontConfig.fontSize.large,
                lineHeight: fontConfig.lineHeight.small,
                css: css`
                    margin-bottom: 0;
                    opacity: 0.7;
                    height: 44px;
                `,
            },
        },
        productDetailDescription: {
            size: fontConfig.fontSize.large,
            lineHeight: fontConfig.lineHeight.small,
            css: css`
                margin-bottom: 0;
                opacity: 0.7;
                height: 44px;
            `,
        },
    },
    productPartNumbersWrapper: {
        css: css`
            margin-bottom: 25px;
        `,
    },
    priceQtywrapper: {
        css: css`
            width: 100%;
            display: flex;
            padding-right: 25px;
            margin-bottom: 10px;
            ${xScreen("lte", 900)} {
                gap: 12px;
                padding-right: 0;
            }
            ${xScreen("lte", "xs")} {
                margin-bottom: 12px;
                flex-wrap: wrap;
                justify-content: space-between;
            }

            &&.not-editable {
                padding-right: 0;
            }

            @media print {
                justify-content: space-between;
                padding-right: 0;
            }
        `,
    },
    productPrice: {
        wrapper: {
            css: css`
                width: calc(100% / 3);
                display: flex;
                align-items: flex-start;
                justify-content: center;
                padding-right: 10px;
                legend {
                    display: none;
                    font-size: ${fontConfig.fontSize.legend}px;
                    font-weight: ${BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold};
                    line-height: ${fontConfig.lineHeight.legend};
                    margin-bottom: 4px;
                }
                ${xScreen("lte", 900)} {
                    width: 100%;
                    max-width: 192px;
                    padding-right: 0;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: flex-end;
                    legend {
                        display: block;
                    }
                }
                ${xScreen("lte", "xs")} {
                    max-width: initial;
                    order: 1;
                    padding-right: 0;
                }
                @media print {
                    order: 1;
                    flex-direction: row;
                    justify-content: flex-start;
                    flex-wrap: wrap;
                    width: 30%;
                    legend {
                        width: 100%;
                        text-align: left;
                        display: block;
                    }
                }
            `,
        },
        errorWrapper: {
            css: css`
                width: calc(100% / 3);
                ${xScreen("lte", 900)} {
                    width: calc(50% - 6px);
                    order: 2;
                }
            `,
        },
        priceWrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
            `,
        },
        price: {
            priceText: {
                size: fontConfig.fontSize.large,
                weight: fontConfig.weight.regular,
                lineHeight: fontConfig.lineHeight.small,
                css: css`
                    word-break: break-word;
                    display: inline-block;
                    & + span {
                        @media print {
                            font-size: ${fontConfig.fontSize.regular}px;
                        }
                    }
                `,
            },
            unitOfMeasureText: {
                size: fontConfig.fontSize.large,
                lineHeight: fontConfig.lineHeight.small,
                weight: fontConfig.weight.regular,
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 180px;
                `,
            },
        },
    },
    quantityGridItem: {
        css: css`
            width: calc(100% / 3);
            legend {
                line-height: ${fontConfig.lineHeight.legend};
                font-weight: ${BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold};
                text-align: left;
            }

            ${xScreen("lte", 900)} {
                width: 100%;
                max-width: 192px;
                & > .editable-qty {
                    max-width: 120px;
                }

                p {
                    text-align: left;
                    padding-left: 3px;
                }
            }
            ${xScreen("lte", "xs")} {
                order: 2;
                width: calc(50% - 6px);
            }
            @media print {
                order: 2;
                display: block;
                width: 30% !important;
                text-align: center;
                legend,
                p,
                span {
                    text-align: center;
                    width: 150px;
                }
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        css: css`
            width: calc(100% / 3);
            display: flex;
            align-items: flex-end;
            flex-direction: column;
            justify-content: center;
            legend {
                display: none;
            }
            ${xScreen("lte", 900)} {
                width: 100%;
                align-items: flex-start;
                legend {
                    display: block;
                    font-size: ${fontConfig.fontSize.legend}px;
                    font-weight: ${BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold};
                    line-height: ${fontConfig.lineHeight.legend};
                    margin-bottom: 4px;
                    text-align: left;
                    text-transform: uppercase;
                }
            }
            ${xScreen("lte", "xs")} {
                order: 3;
                width: calc(50% - 6px);
                align-items: flex-start;
                text-align: right;
            }
            @media print {
                order: 3;
                display: block;
                width: 30% !important;
                text-align: end;
                legend,
                p,
                span {
                    text-align: right;
                    width: 150px;
                }
                legend {
                    display: block;
                    font-size: ${fontConfig.fontSize.legend}px;
                    font-weight: ${BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold};
                    line-height: ${fontConfig.lineHeight.legend};
                    text-align: right;
                    margin-bottom: 4px;
                }
            }
        `,
    },
    extendedUnitNetPriceText: {
        size: fontConfig.fontSize.large,
        lineHeight: fontConfig.lineHeight.small,
        weight: fontConfig.weight.bold,
        css: css`
            padding-left: 10px;
            text-align: right;
            word-break: break-all;
            ${xScreen("lte", 900)} {
                text-align: left;
                padding-left: 0;
            }
            @media print {
                text-align: right;
                display: block;
            }
        `,
    },
    listAndRemoveWrapper: {
        css: css`
            width: 100%;
        `,
    },
    removeCartLineGridItem: {
        css: css`
            position: absolute;
            top: 20px;
            right: 2px;
            ${xScreen("lte", 900)} {
                top: 15px;
            }
        `,
    },
    removeCartLineIcon: {
        src: "Delete",
        size: 16,
        color: "secondary.main",
        css: css`
            &:hover,
            &:focus {
                svg g {
                    color: ${getColor("primary.main")};
                }
            }
        `,
    },
    binInformationsGridItem: {
        width: 12,
    },
    cartLineErrorMessageGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0;
        `,
    },
    cartLineErrorMessageText: {
        color: "danger",
        size: fontConfig.fontSize.xSmall,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        css: css`
            &,
            * {
                color: ${getColor("danger")};
                font-size: ${fontConfig.fontSize.xSmall}px;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            }
        `,
    },
    pickUpOnlyFlag: {
        wrapper: {
            css: css`
                text-align: center;
                line-height: 17px;
                display: flex;
                @media (max-width: 481px) {
                    text-align: left;
                    margin-top: 5px;
                }
            `,
        },
        text: {
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.semiBold,
            lineHeight: fontConfig.lineHeight.singleLineSmall,
            css: css`
                letter-spacing: 0;
            `,
        },
        icon: {
            src: "ReportProblem",
            size: 13,
            color: "warning",
            css: css`
                margin-right: 4px;
            `,
        },
    },
    availabilityContainer: {
        css: css`
            display: flex;
            @media (max-width: 481px) {
                flex-direction: column;
            }
        `,
    },
    availabilityWrapper: {
        css: css`
            flex: 2;
        `,
    },
    pickUpOnlyFlagWrapper: {
        css: css`
            padding-right: 20px;
        `,
    },
    productAvailability: {
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 400px;
            `,
        },
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    // Xng-Change
    editable,
    pickupOnlyLabel,
    isAuth,
    customSettings,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    // Xng-Change
    const [customShowPricing, setCustomShowPricing] = React.useState(false);
    const [signInForPricing, setSignInForPricing] = React.useState(siteMessage("Pricing_SignInForPrice"));

    React.useEffect(() => {
        if (
            (customSettings &&
                customSettings.postalCodeLocationFinder.toLowerCase() === "true" &&
                !window.localStorage.getItem("postalCodeID")?.length &&
                !isAuth) ||
            !showPricing
        ) {
            setCustomShowPricing(false);
        } else {
            setCustomShowPricing(true);
        }
    }, []);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!customShowPricing) {
        const error = signInForPricing || siteMessage("Pricing_SignInForPrice");
        errorMessages.push(error);
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    if (cartLine.properties.pricingFailed?.toLowerCase() === "true") {
        const error = siteMessage("Custom_Price_FailedToLoad");
        errorMessages.push(error);
    }

    if (cart.type === "Quote") {
        if (
            cartLine.qtyOrdered !== null &&
            cartLine.properties.minimumQty &&
            cartLine.qtyOrdered > +cartLine.properties.minimumQty
        ) {
            errorMessages.push(translate("min qty: {0}", cartLine.properties.minimumQty));
        }

        if (
            cartLine.qtyOrdered !== null &&
            cartLine.properties.exactQty &&
            cartLine.qtyOrdered !== +cartLine.properties.exactQty
        ) {
            errorMessages.push(translate("exact qty: {0}", cartLine.properties.exactQty));
        }
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    return (
        <StyledWrapper
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.imageWrapper}>
                    {showImage && (
                        <ProductImage showQuickView={false} product={cartLine} extendedStyles={styles.productImage} />
                    )}
                </StyledWrapper>

                <StyledWrapper {...styles.descAndNumbersWrapper}>
                    <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                        {showProductTitle && (
                            <ProductDescription
                                lines={2}
                                lineHeight={22}
                                product={cartLine}
                                extendedStyles={styles.productDescription}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.productPartNumbersWrapper}>
                        <ProductPartNumbers
                            productNumber={cartLine?.properties?.sku || cartLine?.erpNumber}
                            customerProductNumber={cartLine.customerName}
                            manufacturerItem={cartLine.manufacturerItem}
                            extendedStyles={styles.productPartNumbers}
                            showProductNumber={showProductNumber}
                        />
                    </StyledWrapper>
                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <StyledWrapper {...styles.binInformationsGridItem}>
                            <ProductBinNumbers
                                locationId={cart.vmiLocationId}
                                binId={cartLine.vmiBinId}
                                isCondensed={false}
                                extendedStyles={styles.productBinNumbers}
                            />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.availabilityContainer}>
                        <StyledWrapper {...styles.availabilityWrapper}>
                            {showInventoryAvailability && !cartLine.quoteRequired && (
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    extendedStyles={styles.productAvailability as any}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                />
                            )}
                        </StyledWrapper>
                        {/* {pickupOnlyLabel && (
                            <StyledWrapper {...styles.pickUpOnlyFlagWrapper}>
                                <StyledWrapper {...styles.pickUpOnlyFlag?.wrapper}>
                                    <Icon {...styles.pickUpOnlyFlag?.icon} />
                                    <Typography {...styles.pickUpOnlyFlag?.text}>{pickupOnlyLabel}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )} */}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.priceQtywrapper} className={editable ? "" : "not-editable"}>
                    {!cart.cartNotPriced &&
                        cartLine.properties.pricingFailed?.toLowerCase() !== "true" &&
                        customShowPricing && (
                            <ProductPrice
                                product={cartLine}
                                currencySymbol={cart.currencySymbol}
                                showSavings={false}
                                extendedStyles={styles.productPrice as any}
                                showLabel={showPriceLabel}
                                showUnitOfMeasure={showPerUnitPrice}
                                label={translate("Price/Unit")}
                            />
                        )}
                    {(cartLine.properties.pricingFailed &&
                        cartLine.properties.pricingFailed?.toLowerCase() === "true") ||
                    cart.cartNotPriced ||
                    !customShowPricing ? (
                        <StyledWrapper {...styles.productPrice?.wrapper}>
                            <Typography variant="legend" as="legend">
                                {translate("Price/Unit")}
                            </Typography>
                            <Typography variant="h6">{translate("N/A")}</Typography>
                        </StyledWrapper>
                    ) : (
                        ""
                    )}

                    <StyledWrapper {...styles.quantityGridItem}>
                        <CartLineQuantity
                            cart={cart}
                            editable={editable}
                            onQtyOrderedChange={qtyOrderedChangeHandler}
                            extendedStyles={styles.quantity}
                        />
                    </StyledWrapper>

                    {!cartLine.quoteRequired &&
                        !cart.cartNotPriced &&
                        cartLine.properties.pricingFailed?.toLowerCase() !== "true" &&
                        cartLine.pricing &&
                        customShowPricing && (
                            <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                                <Typography variant="legend" as="legend">
                                    {translate("Total")}
                                </Typography>
                                <Typography
                                    {...styles.extendedUnitNetPriceText}
                                    data-test-selector="cartline_extendedUnitNetPrice"
                                >
                                    {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                                {enableVat && (
                                    <>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {vatPriceDisplay === "DisplayWithVat" ||
                                            vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                : translate("Ex. VAT")}
                                        </Typography>
                                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <Typography {...styles.subtotalWithoutVatText}>
                                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                </Typography>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {translate("Ex. VAT")}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </StyledWrapper>
                        )}
                    {cartLine.properties.pricingFailed?.toLowerCase() === "true" ||
                    cart.cartNotPriced ||
                    !customShowPricing ? (
                        <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                            <Typography as="legend">{translate("Total")}</Typography>
                            <Typography {...styles.extendedUnitNetPriceText}>{translate("N/A")}</Typography>
                        </StyledWrapper>
                    ) : (
                        ""
                    )}
                </StyledWrapper>

                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Typography
                                {...styles.cartLineErrorMessageText}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                data-test-selector="cartline_errorMessage"
                            >
                                {!customShowPricing && message === signInForPricing ? (
                                    <RedirectToSignIn>{message}</RedirectToSignIn>
                                ) : (
                                    message
                                )}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.listAndRemoveWrapper}>
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        {...({
                            showLineNotes:
                                showLineNotes &&
                                !cartLine.isPromotionItem &&
                                cart.properties["isPunchout"] === undefined,
                        } as any)}
                        showAddToList={
                            !hideAddToList && cartLine.canAddToWishlist && showAddToList && !cartLine.isRestricted
                        }
                        addToListHandler={addToListClickHandler}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.removeCartLineGridItem}>
                {showRemoveAction && editable && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
